import { useNavigate } from 'react-router-dom'
import {
    ShoppingCart,
    Sparkles,
    ListChecks,
    ChefHat,
    Package,
    ArrowRight,
    Clock,
    Users,
    Zap,
    CheckCircle2,
} from 'lucide-react'

const features = [
    {
        icon: ChefHat,
        title: 'Recettes intelligentes',
        desc: 'Recherchez parmi des recettes existantes ou laissez l\'IA trouver les ingrédients pour vous.',
        color: 'var(--primary-light)',
    },
    {
        icon: ListChecks,
        title: 'Listes optimisées',
        desc: 'Les quantités sont calculées automatiquement selon vos personnes, durée et repas par jour.',
        color: 'var(--success)',
    },
    {
        icon: Package,
        title: 'Inventaire maison',
        desc: 'Renseignez ce que vous avez déjà — les articles en stock sont déduits de la liste.',
        color: 'var(--accent)',
    },
    {
        icon: Sparkles,
        title: 'Recherche IA',
        desc: 'Tapez n\'importe quel plat et notre IA génère la liste d\'ingrédients en quelques secondes.',
        color: '#a78bfa',
    },
]

const steps = [
    { num: '01', title: 'Choisissez vos plats', desc: 'Sélectionnez des recettes ou cherchez avec l\'IA' },
    { num: '02', title: 'Configurez vos paramètres', desc: 'Nombre de personnes, durée, repas par jour' },
    { num: '03', title: 'Générez et faites vos courses', desc: 'La liste est prête, cochez au fur et à mesure !' },
]

export default function LandingPage() {
    const navigate = useNavigate()

    return (
        <div style={{ overflow: 'hidden' }}>
            {/* ─── Hero ────────────────────────────────────────────── */}
            <section style={{
                minHeight: '100dvh',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
                padding: '80px 24px 60px',
                position: 'relative',
            }}>
                {/* Decorative glow orbs */}
                <div style={{
                    position: 'absolute',
                    width: 500,
                    height: 500,
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(99, 102, 241, 0.15), transparent 70%)',
                    top: -100,
                    right: -150,
                    pointerEvents: 'none',
                }} />
                <div style={{
                    position: 'absolute',
                    width: 400,
                    height: 400,
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(249, 115, 22, 0.1), transparent 70%)',
                    bottom: 0,
                    left: -100,
                    pointerEvents: 'none',
                }} />

                <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '6px 16px',
                    borderRadius: 99,
                    background: 'var(--primary-glow)',
                    border: '1px solid rgba(99, 102, 241, 0.2)',
                    fontSize: '0.82rem',
                    fontWeight: 600,
                    color: 'var(--primary-light)',
                    marginBottom: 28,
                    animation: 'fadeIn 0.6s ease',
                }}>
                    <Zap size={14} /> Propulsé par l'IA
                </div>

                <h1 style={{
                    fontSize: 'clamp(2.4rem, 7vw, 4rem)',
                    fontWeight: 900,
                    lineHeight: 1.1,
                    letterSpacing: '-0.03em',
                    maxWidth: 700,
                    animation: 'fadeIn 0.7s ease 0.1s both',
                }}>
                    Vos courses,{' '}
                    <span className="hero-gradient">simplifiées.</span>
                </h1>

                <p style={{
                    fontSize: 'clamp(1rem, 2.5vw, 1.2rem)',
                    color: 'var(--text-2)',
                    maxWidth: 520,
                    marginTop: 20,
                    lineHeight: 1.7,
                    animation: 'fadeIn 0.7s ease 0.2s both',
                }}>
                    Générez des listes de courses intelligentes à partir de vos recettes préférées.
                    Plus de temps perdu, plus d'oublis.
                </p>

                <div style={{
                    display: 'flex',
                    gap: 12,
                    marginTop: 36,
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    animation: 'fadeIn 0.7s ease 0.35s both',
                }}>
                    <button
                        className="btn btn-primary"
                        style={{ padding: '16px 32px', fontSize: '1rem', borderRadius: 99 }}
                        onClick={() => navigate('/login')}
                    >
                        <ShoppingCart size={20} />
                        Commencer gratuitement
                    </button>
                    <a
                        href="#features"
                        className="btn btn-ghost"
                        style={{ padding: '16px 28px', fontSize: '1rem', borderRadius: 99 }}
                    >
                        Découvrir
                        <ArrowRight size={16} />
                    </a>
                </div>

                {/* Stats */}
                <div style={{
                    display: 'flex',
                    gap: 32,
                    marginTop: 56,
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    animation: 'fadeIn 0.7s ease 0.5s both',
                }}>
                    {[
                        { icon: Users, val: '100%', label: 'Gratuit' },
                        { icon: Clock, val: '< 2 min', label: 'Pour générer' },
                        { icon: Sparkles, val: 'IA', label: 'Intégrée' },
                    ].map((s, i) => (
                        <div key={i} style={{ textAlign: 'center' }}>
                            <s.icon size={18} style={{ color: 'var(--primary-light)', marginBottom: 6 }} />
                            <div style={{ fontWeight: 800, fontSize: '1.3rem' }}>{s.val}</div>
                            <div style={{ fontSize: '0.78rem', color: 'var(--text-3)' }}>{s.label}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* ─── Features ────────────────────────────────────────── */}
            <section id="features" style={{
                padding: '80px 24px',
                maxWidth: 900,
                margin: '0 auto',
            }}>
                <p style={{
                    textAlign: 'center',
                    fontSize: '0.82rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.12em',
                    color: 'var(--primary-light)',
                    marginBottom: 12,
                }}>Fonctionnalités</p>
                <h2 style={{
                    textAlign: 'center',
                    fontSize: 'clamp(1.5rem, 4vw, 2.2rem)',
                    fontWeight: 800,
                    letterSpacing: '-0.02em',
                    marginBottom: 48,
                }}>Tout ce qu'il faut pour vos courses</h2>

                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                    gap: 16,
                }}>
                    {features.map((f, i) => (
                        <div
                            key={i}
                            className="card"
                            style={{
                                padding: 24,
                                animation: `slideUp 0.4s ease ${i * 0.08}s both`,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 14,
                            }}
                        >
                            <div style={{
                                width: 44,
                                height: 44,
                                borderRadius: 12,
                                background: `color-mix(in srgb, ${f.color} 12%, transparent)`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}>
                                <f.icon size={22} color={f.color} />
                            </div>
                            <h3 style={{ fontSize: '1.05rem', fontWeight: 700 }}>{f.title}</h3>
                            <p style={{ fontSize: '0.88rem', color: 'var(--text-2)', lineHeight: 1.6 }}>{f.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* ─── How It Works ────────────────────────────────────── */}
            <section style={{
                padding: '80px 24px',
                maxWidth: 700,
                margin: '0 auto',
            }}>
                <p style={{
                    textAlign: 'center',
                    fontSize: '0.82rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.12em',
                    color: 'var(--accent)',
                    marginBottom: 12,
                }}>Comment ça marche</p>
                <h2 style={{
                    textAlign: 'center',
                    fontSize: 'clamp(1.5rem, 4vw, 2.2rem)',
                    fontWeight: 800,
                    letterSpacing: '-0.02em',
                    marginBottom: 48,
                }}>3 étapes, c'est tout</h2>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                    {steps.map((s, i) => (
                        <div
                            key={i}
                            className="card"
                            style={{
                                padding: '20px 24px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 20,
                                animation: `slideUp 0.35s ease ${i * 0.1}s both`,
                            }}
                        >
                            <span className="gradient-text" style={{ fontSize: '2rem', fontWeight: 900, flexShrink: 0, width: 50 }}>{s.num}</span>
                            <div>
                                <h3 style={{ fontSize: '1.05rem', fontWeight: 700, marginBottom: 4 }}>{s.title}</h3>
                                <p style={{ fontSize: '0.88rem', color: 'var(--text-2)' }}>{s.desc}</p>
                            </div>
                            <CheckCircle2 size={20} color="var(--success)" style={{ flexShrink: 0, marginLeft: 'auto' }} />
                        </div>
                    ))}
                </div>
            </section>

            {/* ─── Final CTA ───────────────────────────────────────── */}
            <section style={{
                padding: '80px 24px 120px',
                textAlign: 'center',
            }}>
                <div className="info-card" style={{
                    maxWidth: 600,
                    margin: '0 auto',
                    padding: '48px 32px',
                    textAlign: 'center',
                }}>
                    <h2 style={{
                        fontSize: 'clamp(1.4rem, 4vw, 2rem)',
                        fontWeight: 800,
                        letterSpacing: '-0.02em',
                        marginBottom: 14,
                    }}>
                        Prêt à simplifier vos courses ?
                    </h2>
                    <p style={{
                        fontSize: '1rem',
                        color: 'var(--text-2)',
                        maxWidth: 400,
                        margin: '0 auto 28px',
                        lineHeight: 1.6,
                    }}>
                        Gratuit, et propulsé par l'intelligence artificielle.
                    </p>
                    <button
                        className="btn btn-primary"
                        style={{ padding: '16px 36px', fontSize: '1rem', borderRadius: 99 }}
                        onClick={() => navigate('/login')}
                    >
                        <ShoppingCart size={20} />
                        Lancer EasyMarket
                    </button>
                </div>
            </section>

            {/* ─── Footer ──────────────────────────────────────────── */}
            <footer style={{
                borderTop: '1px solid var(--border)',
                padding: '24px',
                textAlign: 'center',
                color: 'var(--text-3)',
                fontSize: '0.8rem',
            }}>
                <span className="hero-gradient" style={{ fontWeight: 700, fontSize: '0.9rem' }}>EasyMarket</span>
                <span style={{ margin: '0 8px' }}>·</span>
                © {new Date().getFullYear()} — Tous droits réservés
            </footer>
        </div>
    )
}
